import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "@/lib/notifications";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import {
  TrendUp, TrendDown, CurrencyEur, Target, Files, Ticket, UserPlus, Warning, ChatCircle, ArrowRight, Bell, Users, Info, Trash, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DemoBanner, HelpTip } from "@/components/DemoHelpers";
import { DashboardTecnica, DashboardNotaio, DashboardAgenzia } from "./dashboards/CategoryDashboards";
import DashboardTour from "@/components/DashboardTour";
import { PWADashboardNudge } from "@/components/PWADashboardNudge";
import { MobileNotificationsFeed } from "@/components/MobileNotificationsFeed";
import UpcomingDeadlinesWidget from "@/components/UpcomingDeadlinesWidget";
import EntityFeesPendingBanner from "@/components/EntityFeesPendingBanner";
import { TodayAppointmentsWidget } from "@/components/TodayAppointmentsWidget";

const NAVY = "#4F46E5";
const TERRA = "#6D28D9";
const GREEN = "#166534";
const PURPLE = "#7C3AED";
const YELLOW = "#CA8A04";
const RED = "#991B1B";
const COLORS = [NAVY, TERRA, GREEN, YELLOW, RED, "#52525B"];

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
const pct = (a, b) => (b ? ((a - b) / b) * 100 : 0);

function InfoPopover({ text, testid }) {
  const stop = (e) => e.stopPropagation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label="Informazioni"
          onClick={stop}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-purple-700 hover:bg-purple-50 cursor-pointer shrink-0"
          data-testid={`${testid}-info-btn`}
        >
          <Info size={13} weight="bold" />
        </span>
      </PopoverTrigger>
      <PopoverContent onClick={stop} className="w-64 text-xs leading-relaxed normal-case tracking-normal" data-testid={`${testid}-info-popover`}>
        {text}
      </PopoverContent>
    </Popover>
  );
}

function MiniStatCard({ label, value, sub, icon: Icon, color = "#4F46E5", onClick, testid, tooltip, infoText }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={tooltip}
      className="group aspect-square md:aspect-auto md:min-h-[110px] flex flex-col justify-between p-3 md:p-4 rounded-xl bg-white border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all text-left focus:outline-none focus:ring-2 focus:ring-purple-300"
    >
      <div className="flex items-start justify-between">
        <div className="label-eyebrow text-[10px] md:text-[11px] leading-tight pr-1 flex items-center gap-1">
          {label}
          {infoText && <InfoPopover text={infoText} testid={testid} />}
        </div>
        {Icon && (
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-[color:hsl(var(--surface-alt))] flex items-center justify-center shrink-0">
            <Icon size={14} color={color} weight="regular" />
          </div>
        )}
      </div>
      <div>
        <div className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-none" style={{ color }}>
          {value}
        </div>
        {sub && (
          <div className="text-[10px] md:text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-tight">{sub}</div>
        )}
      </div>
    </button>
  );
}

function KpiCard({ label, value, sub, prev, icon: Icon, testid, color = NAVY, tooltip }) {
  const delta = prev !== undefined ? pct(value, prev) : null;
  const positive = delta > 0;
  return (
    <div
      className="kpi-card fade-up aspect-square md:aspect-auto md:min-h-[110px] flex flex-col justify-between !p-3 md:!p-4 min-w-0"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="label-eyebrow text-[10px] md:text-[11px] leading-tight pr-1 flex items-center gap-1">
          {label}
          {tooltip && <HelpTip text={tooltip} />}
        </div>
        {Icon && (
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-[color:hsl(var(--surface-alt))] flex items-center justify-center shrink-0">
            <Icon size={14} color={color} weight="regular" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-none break-words" style={{ color }}>
          {typeof value === "number" && label.toLowerCase().includes("fatt") ? fmt(value) : fmtN(value)}
        </div>
        {sub && <div className="text-[10px] md:text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-tight">{sub}</div>}
        {delta !== null && (
          <div className={`flex items-center gap-1 text-[10px] md:text-xs font-semibold mt-1 md:mt-2 ${positive ? "text-green-700" : delta < 0 ? "text-red-700" : "text-muted-foreground"}`}>
            {positive ? <TrendUp size={12} /> : delta < 0 ? <TrendDown size={12} /> : null}
            {delta ? `${delta > 0 ? "+" : ""}${delta.toFixed(1)}% vs mese prec.` : "—"}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { items: notifItems } = useNotifications();
  const [data, setData] = useState(null);
  const [opData, setOpData] = useState(null);

  // Iter209 — Welcome toast dopo la selezione dell'abbonamento al primo
  // accesso Pro Master. Il flag arriva via location.state dal
  // SubscriptionOnboarding e viene consumato una sola volta.
  useEffect(() => {
    const w = location.state?.subscriptionWelcome;
    if (!w) return;
    const planName = w.plan_name || "gratuito";
    const first = (user?.first_name || "").trim();
    toast.success(
      `Benvenuto su Housetag${first ? `, ${first}` : ""}! Il tuo piano ${planName} è attivo. Puoi cambiarlo in qualsiasi momento dalle Impostazioni.`,
      { duration: 8000, id: "subscription-welcome" }
    );
    // Pulisce lo state per non ripetere il toast a refresh
    nav(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Dashboard specifica per categoria professionale (iter50). Se la
  // categoria dell'utente ha `dashboard_type` in {"tecnica","notaio","agenzia"}
  // renderizziamo il componente dedicato. Altrimenti fallback sulla
  // dashboard standard sotto.
  const [categoryDashType, setCategoryDashType] = useState(null);

  // Iter208.47 — Andamento Fatturato flessibile.
  // trendPreset: "m3" | "m6" | "m12" | "year" | "custom"
  // trendCompare: 0 (off) | 1..3 (anni fa)
  const [trendPreset, setTrendPreset] = useState("m12");
  const [trendStart, setTrendStart] = useState("");
  const [trendEnd, setTrendEnd] = useState("");
  const [trendCompare, setTrendCompare] = useState(0);
  const [trendData, setTrendData] = useState(null);

  // Iter140v: al mount della Dashboard e ad ogni ritorno di focus, rifetcha
  // i dati utente (`/auth/me`) così se un admin cambia plafond/fascia vediamo
  // subito il valore aggiornato senza logout/login.
  useEffect(() => {
    if (!refreshUser) return;
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.role === "admin" || user.is_supervisor || user.role === "accounting_operator") {
      api.get("/dashboard/admin").then(r => setData(r.data));
    }
    if (user.role !== "professional" && user.role !== "admin") {
      api.get("/dashboard/operator").then(r => setOpData(r.data));
    }
    if (user.role === "professional") {
      api.get("/practices").then(r => setOpData({ practices: r.data }));
      // Recupera il dashboard_type della categoria del PRO (o del suo parent
      // se è un sub-user). Best-effort: se fallisce → fallback standard.
      if (user.professional_category_id) {
        api.get("/professional-categories")
          .then(r => {
            const cat = (r.data || []).find(c => c._id === user.professional_category_id);
            setCategoryDashType(cat?.dashboard_type || "");
          })
          .catch(() => setCategoryDashType(""));
      } else {
        setCategoryDashType("");
      }
    }
  }, [user]);

  // Iter208.47 — Fetch trend fatturato flessibile con preset/range/confronto.
  useEffect(() => {
    if (!(user.role === "admin" || user.is_supervisor || user.role === "accounting_operator")) return;
    const params = {};
    if (trendPreset === "m3") params.months = 3;
    else if (trendPreset === "m6") params.months = 6;
    else if (trendPreset === "m12") params.months = 12;
    else if (trendPreset === "year") { /* default range */ }
    else if (trendPreset === "custom") {
      if (!trendStart || !trendEnd) return;
      params.start_date = trendStart;
      params.end_date = trendEnd;
    }
    if (trendCompare > 0) params.compare_year_offset = trendCompare;
    api.get("/dashboard/admin/revenue-trend", { params })
      .then((r) => setTrendData(r.data))
      .catch(() => setTrendData(null));
  }, [user, trendPreset, trendStart, trendEnd, trendCompare]);

  // Merge trend + compare in un unico dataset chart-friendly.
  const mergedTrend = useMemo(() => {
    if (!trendData || !Array.isArray(trendData.trend)) return [];
    const base = trendData.trend.map((row) => ({ month: row.month, revenue: row.revenue }));
    if (trendData.compare && Array.isArray(trendData.compare)) {
      for (let i = 0; i < base.length; i += 1) {
        if (trendData.compare[i]) base[i].compare = trendData.compare[i].revenue;
      }
    }
    return base;
  }, [trendData]);

  const trendLabel = trendData?.period_label || "Andamento fatturato";

  // Dispatch dashboard specifica per categoria (solo per role=professional).
  if (user.role === "professional" && categoryDashType) {
    if (categoryDashType === "tecnica") return <><DashboardTour /><PWADashboardNudge /><DashboardTecnica /></>;
    if (categoryDashType === "notaio") return <><DashboardTour /><PWADashboardNudge /><DashboardNotaio /></>;
    if (categoryDashType === "agenzia") return <><DashboardTour /><PWADashboardNudge /><DashboardAgenzia /></>;
    // "" o valore ignoto → cade sulla dashboard standard sotto
  }

  if (user.role === "professional") {
    const plafondUsed = Number(user.plafond_used_current_month || 0);
    // Iter140v: nessun fallback hardcoded. Se il backend non fornisce il limite
    // (edge case: plafond assente O configurazione incompleta) mostriamo 0
    // per far notare il problema invece di nascondere un valore sbagliato.
    const plafondLimit = Number(user.plafond_monthly_limit || 0);
    const credit = Number(user.accumulated_credit || 0);
    const fmtEur = (n) => `€ ${Math.round(n).toLocaleString("it-IT")}`;
    return (
      <div>
        <DashboardTour />
        <DemoBanner />
        <PWADashboardNudge />
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="label-eyebrow">Ciao {user.first_name}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
              La tua dashboard
              <HelpTip text="Da qui vedi lo stato delle pratiche, il plafond mensile utilizzato e le ultime notifiche. Naviga tra Immobili e Marketplace per aggiungere nuovi asset." />
            </h1>
            <p className="text-muted-foreground mt-2 text-sm md:text-base">Gestisci immobili, servizi acquistati e pratiche in corso.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:w-[420px] shrink-0" data-testid="dashboard-header-kpis">
          <MiniStatCard
            label="Plafond mensile"
            value={fmtEur(plafondUsed)}
            sub={`Limite €${plafondLimit.toLocaleString("it-IT")}`}
            icon={CurrencyEur}
            color={PURPLE}
            testid="kpi-plafond"
            infoText="Il plafond è il budget mensile massimo che puoi spendere in servizi. Viene assegnato dall'amministratore e si azzera ogni mese."
            tooltip={`Importo massimo utilizzabile ogni mese per l'acquisto di servizi. Plafond di €${plafondLimit}`}
          />
          <MiniStatCard
            label="Credito"
            value={fmtEur(credit)}
            sub="Da usare sulle prossime pratiche"
            icon={CurrencyEur}
            color={TERRA}
            testid="kpi-credit"
            infoText="Il credito è il saldo disponibile sul tuo account. Lo maturi con la condivisione delle pratiche. Viene scalato ad ogni acquisto di servizio o pratica."
            tooltip="Credito accumulato dalla vendita di servizi, utilizzabile per gli acquisti."
          />
          </div>
        </div>
        <UpcomingDeadlinesWidget />
        <EntityFeesPendingBanner />
        <MobileNotificationsFeed />
        <TodayAppointmentsWidget />
        <ActivityFeed items={notifItems} nav={nav} />
      </div>
    );
  }

  if ((user.role === "admin" || user.is_supervisor) && data) {
    const k = data.kpis;
    return (
      <div>
        <PWADashboardNudge />
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="label-eyebrow">Dashboard</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Panoramica generale</h1>
            <p className="text-muted-foreground mt-2">KPI, andamento fatturato, pratiche e obiettivi annuali</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
          <KpiCard label="Fatturato Mese" value={k.revenue_month} prev={k.revenue_prev_month} icon={CurrencyEur} testid="kpi-rev-month" color={NAVY} />
          <KpiCard label="Fatturato Anno" value={k.revenue_year} sub={`Obiettivo: ${fmt(k.annual_target)}`} icon={Target} testid="kpi-rev-year" color={TERRA} />
          <KpiCard label="Pratiche Aperte" value={k.open_practices} sub={`${k.completed_practices} concluse`} icon={Files} testid="kpi-open" color={GREEN} />
          <KpiCard label="Ticket Medio" value={k.ticket_avg} sub={`${k.sold_year} servizi anno`} icon={Ticket} testid="kpi-ticket" color={YELLOW} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="kpi-card md:col-span-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <div className="label-eyebrow">Andamento Fatturato</div>
                <h3 className="font-display text-xl font-semibold mt-1" data-testid="rev-period-label">
                  {trendLabel}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={trendPreset}
                  onChange={(e) => setTrendPreset(e.target.value)}
                  className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                  data-testid="rev-period-select"
                >
                  <option value="m3">Ultimi 3 mesi</option>
                  <option value="m6">Ultimi 6 mesi</option>
                  <option value="m12">Ultimi 12 mesi</option>
                  <option value="year">Anno corrente</option>
                  <option value="custom">Range personalizzato</option>
                </select>
                {trendPreset === "custom" && (
                  <>
                    <input
                      type="date"
                      value={trendStart}
                      onChange={(e) => setTrendStart(e.target.value)}
                      className="text-xs border border-slate-300 rounded px-2 py-1"
                      data-testid="rev-period-start"
                    />
                    <input
                      type="date"
                      value={trendEnd}
                      onChange={(e) => setTrendEnd(e.target.value)}
                      className="text-xs border border-slate-300 rounded px-2 py-1"
                      data-testid="rev-period-end"
                    />
                  </>
                )}
                <label className="text-xs text-slate-600 inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={trendCompare > 0}
                    onChange={(e) => setTrendCompare(e.target.checked ? 1 : 0)}
                    data-testid="rev-compare-toggle"
                  />
                  Confronta con
                </label>
                {trendCompare > 0 && (
                  <select
                    value={trendCompare}
                    onChange={(e) => setTrendCompare(Number(e.target.value))}
                    className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                    data-testid="rev-compare-years"
                  >
                    <option value={1}>1 anno fa</option>
                    <option value={2}>2 anni fa</option>
                    <option value={3}>3 anni fa</option>
                  </select>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={mergedTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#52525B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#52525B" }} tickFormatter={v => `€${v/1000}k`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Line type="monotone" dataKey="revenue" name="Periodo" stroke={NAVY} strokeWidth={2.5} dot={{ r: 3, fill: TERRA }} />
                {trendCompare > 0 && (
                  <Line type="monotone" dataKey="compare" name={`${trendCompare} anno${trendCompare > 1 ? "i" : ""} fa`} stroke={TERRA} strokeDasharray="5 5" strokeWidth={2} dot={false} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="kpi-card hidden md:block" data-testid="dashboard-status-distribution">
            <div className="label-eyebrow">Pratiche per stato</div>
            <h3 className="font-display text-lg font-semibold mt-1 mb-2">Distribuzione</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.status_distribution} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                  {data.status_distribution.map((item, i) => <Cell key={item.name || `cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {data.status_distribution.slice(0, 4).map((s, i) => (
                <div key={s.name} className="flex justify-between text-xs">
                  <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name}</span>
                  <span className="font-mono font-semibold">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
          <div className="kpi-card col-span-2 md:col-span-1">
            <div className="label-eyebrow">Obiettivo Annuale</div>
            <div className="font-display text-3xl font-bold mt-2">{k.goal_progress_pct.toFixed(1)}%</div>
            <div className="mt-3 h-2 bg-[color:hsl(var(--surface-alt))] rounded-full overflow-hidden">
              <div className="h-full ht-gradient border-0 transition-all" style={{ width: `${Math.min(k.goal_progress_pct, 100)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {fmt(k.revenue_year)} / {fmt(k.annual_target)}
            </div>
          </div>
          <KpiCard label="Servizi Anno" value={k.sold_year} sub={`${k.sold_month} nel mese`} icon={Ticket} color={NAVY} />
          <KpiCard label="Nuovi Clienti Mese" value={k.new_clients_month} icon={UserPlus} color={TERRA} />
        </div>

        {data.critical_assignments.length > 0 && (
          <div className="kpi-card border-l-4 border-l-purple-500 mb-8" data-testid="critical-alerts">
            <div className="flex items-center gap-3 mb-4">
              <Warning size={22} color={TERRA} weight="fill" />
              <div>
                <div className="label-eyebrow" style={{ color: TERRA }}>Assegnazioni critiche</div>
                <h3 className="font-display text-xl font-semibold">Richiedono intervento manuale</h3>
              </div>
            </div>
            <div className="space-y-2">
              {data.critical_assignments.slice(0, 5).map(p => (
                <div key={p._id} onClick={() => nav(`/practices/${p._id}`)} className="flex items-center justify-between border-b py-2 text-sm cursor-pointer hover:bg-[color:hsl(var(--surface-alt))] px-2 -mx-2 rounded">
                  <div>
                    <div className="font-medium">{p.service_name}</div>
                    <div className="text-xs text-muted-foreground">{p.property_address || "Nessun indirizzo"} · {p.status}</div>
                  </div>
                  <span className="role-badge bg-red-50 text-red-800">{p.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Feed - recent notifications
            Iter209ce — Nascosto su mobile per snellire la dashboard admin. */}
        <div className="kpi-card hidden md:block" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Bell size={22} color={NAVY} weight="regular" />
              <div>
                <div className="label-eyebrow">Attività recente</div>
                <h3 className="font-display text-xl font-semibold">Ultimi eventi sulla piattaforma</h3>
              </div>
            </div>
            <button onClick={() => nav("/board")} className="text-xs text-purple-700 font-semibold hover:underline flex items-center gap-1">
              Vai alla bacheca <ArrowRight size={12} />
            </button>
          </div>
          {notifItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nessuna attività recente</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {notifItems.slice(0, 15).map(n => {
                const validUrl = n.target_url && n.target_url !== "/" && n.target_url.startsWith("/");
                const cls = `flex items-start gap-3 p-3 rounded-md transition-colors ${
                  validUrl ? "cursor-pointer" : "cursor-default"
                } ${!n.is_read ? "bg-purple-50/70 " + (validUrl ? "hover:bg-purple-100/70" : "") : validUrl ? "hover:bg-[color:hsl(var(--surface-alt))]" : ""}`;
                return (
                  <div
                    key={n._id}
                    onClick={validUrl ? () => nav(n.target_url) : undefined}
                    className={cls}
                    data-testid={`activity-${n._id}`}
                  >
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" />}
                    {n.is_read && <span className="w-2 h-2 mt-2 shrink-0" />}
                    <ChatCircle size={18} color={n.type === "user_registration" ? "#166534" : n.type === "practice_message" ? "#6D28D9" : "#4F46E5"} weight="duotone" className="shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{n.title}</div>
                      {(n.practice_address || n.practice_client) ? (
                    <div className="text-xs text-muted-foreground" data-testid={`activity-revoked-info-${n._id}`}>
                      {n.practice_address && <div className="line-clamp-1">{n.practice_address}</div>}
                      {n.practice_client && <div className="line-clamp-1">{n.practice_client}</div>}
                    </div>
                  ) : n.body && <div className="text-xs text-muted-foreground line-clamp-1">{n.body}</div>}
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                        {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {validUrl && <ArrowRight size={14} className="text-muted-foreground mt-1" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Operator dashboard
  if (opData) {
    const goKanban = (bucket) => nav(`/board?filter=${bucket}`);
    return (
      <div>
        <PWADashboardNudge />
        <div className="mb-6 md:mb-8">
          <div className="label-eyebrow">Ciao {user.first_name}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Le tue lavorazioni</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <MiniStatCard
            label="In coda"
            value={opData.queued || 0}
            icon={Users}
            color={NAVY}
            onClick={() => goKanban("altro_operatore")}
            testid="op-queued"
          />
          <MiniStatCard
            label="Assegnazione"
            value={opData.assigned || 0}
            icon={Files}
            color={PURPLE}
            onClick={() => goKanban("assegnazione")}
            testid="op-assigned"
          />
          <MiniStatCard
            label="In lavorazione"
            value={opData.in_progress || 0}
            icon={Ticket}
            color={YELLOW}
            onClick={() => goKanban("in_lavorazione")}
            testid="op-in-progress"
          />
          <MiniStatCard
            label="Completate"
            value={opData.completed_month || 0}
            icon={Ticket}
            color={GREEN}
            onClick={() => goKanban("completata")}
            testid="op-completed"
          />
        </div>
        <div className="kpi-card">
          <h3 className="font-display text-xl font-semibold mb-4">Le tue pratiche</h3>
          <div className="space-y-2">
            {(opData.practices || []).map(p => (
              <div key={p._id} onClick={() => nav(`/practices/${p._id}`)} className="flex items-center justify-between border-b py-3 cursor-pointer hover:bg-[color:hsl(var(--surface-alt))] px-2 -mx-2 rounded">
                <div>
                  <div className="font-medium">{p.service_name}</div>
                  <div className="text-xs text-muted-foreground">{p.property_address || "N/A"} · {p.current_step_name}</div>
                </div>
                <span className="role-badge" style={{ background: "#F3F0EC" }}>{p.status}</span>
              </div>
            ))}
            {(opData.practices || []).length === 0 && <p className="text-sm text-muted-foreground">Nessuna pratica assegnata.</p>}
          </div>
        </div>
        <div className="mt-6">
          <ActivityFeed items={notifItems} nav={nav} />
        </div>
      </div>
    );
  }

  return <div className="p-8">Caricamento...</div>;
}

function ActivityRowActions({ n, onDone }) {
  const run = async (e, path) => {
    e.stopPropagation();
    try {
      await api.post(path, { ids: [n._id] });
      await onDone();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };
  const btn = "w-7 h-7 rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-white hover:text-slate-900";
  return (
    <div className="ml-auto shrink-0 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(e) => run(e, n.is_read ? "/notifications/mark-unread" : "/notifications/mark-read")}
        className={btn}
        title={n.is_read ? "Segna come non letta" : "Segna come letta"}
        data-testid={`activity-toggle-read-${n._id}`}
      >
        {n.is_read ? <EyeSlash size={15} /> : <Eye size={15} />}
      </button>
      <button
        type="button"
        onClick={(e) => run(e, "/notifications/delete")}
        className={`${btn} hover:text-red-600`}
        title="Elimina notifica"
        data-testid={`activity-delete-${n._id}`}
      >
        <Trash size={15} />
      </button>
    </div>
  );
}

function ActivityFeed({ items, nav }) {
  const { refresh } = useNotifications();
  // Iter209di — Nascosto su mobile per snellire tutte le dashboard
  // (pro, operatori, admin). Resta visibile da `md` in su.
  return (
    <div className="kpi-card hidden md:block" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Bell size={22} color="#4F46E5" weight="regular" />
          <div>
            <div className="label-eyebrow">Attività recente</div>
            <h3 className="font-display text-xl font-semibold">Ultimi eventi</h3>
          </div>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Nessuna attività recente</p>
      ) : (
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {items.slice(0, 15).map(n => {
            const validUrl = n.target_url && n.target_url !== "/" && n.target_url.startsWith("/");
            const cls = `group flex items-start gap-3 p-3 rounded-md transition-colors ${
              validUrl ? "cursor-pointer" : "cursor-default"
            } ${!n.is_read ? "bg-purple-50/70 " + (validUrl ? "hover:bg-purple-100/70" : "") : validUrl ? "hover:bg-[color:hsl(var(--surface-alt))]" : ""}`;
            return (
              <div
                key={n._id}
                onClick={validUrl ? () => nav(n.target_url) : undefined}
                className={cls}
                data-testid={`activity-${n._id}`}
              >
                {!n.is_read ? <span className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" /> : <span className="w-2 h-2 mt-2 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  {(n.practice_address || n.practice_client) ? (
                    <div className="text-xs text-muted-foreground" data-testid={`activity-revoked-info-${n._id}`}>
                      {n.practice_address && <div className="line-clamp-1">{n.practice_address}</div>}
                      {n.practice_client && <div className="line-clamp-1">{n.practice_client}</div>}
                    </div>
                  ) : n.body && <div className="text-xs text-muted-foreground line-clamp-1">{n.body}</div>}
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                    {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <ActivityRowActions n={n} onDone={refresh} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
